import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { Company } from '../types';
import { api } from '../../../core/utils/apiClient';

type ViewMode = 'table' | 'board' | 'list';
type TabType = 'all' | 'african' | 'non-african';

export const CompaniesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setIsLoading(true);

      const response = await api.get('/api/v1/companies/');

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const africanCountries = ['Nigeria', 'Kenya', 'South Africa', 'Egypt', 'Ghana', 'Morocco', 'Tanzania', 'Uganda', 'Algeria', 'Tunisia', 'Ethiopia', 'Senegal', 'Cameroon', 'Ivory Coast', 'Rwanda', 'Zambia'];

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = [
      COLORS.user.primary,
      COLORS.extended.orange,
      COLORS.extended.azure,
      COLORS.primary.deepSoftLime,
      COLORS.extended.lavenderBlue,
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const getStatusBadge = (isActive: boolean, index: number) => {
    const statuses = ['Active', 'Prospect', 'Inactive'];
    const status = isActive ? statuses[index % 3] : 'Inactive';
    
    const statusColors: Record<string, { bg: string; text: string }> = {
      Active: { bg: '#E8F5E9', text: '#2E7D32' },
      Prospect: { bg: '#FFF3E0', text: '#E65100' },
      Inactive: { bg: '#F5F5F5', text: '#616161' },
    };

    return { status, ...statusColors[status] };
  };

  const getEmployeeRange = (employees: string | undefined) => {
    if (!employees) return { range: '1K-3K', bg: '#E8F4F8', text: '#1976D2' };
    
    const ranges = [
      { range: '100K+', bg: '#F3E5F5', text: '#7B1FA2' },
      { range: '10K-15K', bg: '#E8F4F8', text: '#1976D2' },
      { range: '5K-8K', bg: '#E8F5E9', text: '#2E7D32' },
      { range: '1K-3K', bg: '#FFF3E0', text: '#E65100' },
      { range: '15K-50K', bg: '#E8F5E9', text: '#2E7D32' },
      { range: '25K-55K', bg: '#E3F2FD', text: '#1565C0' },
      { range: '120K-1M', bg: '#F3E5F5', text: '#7B1FA2' },
      { range: '10K-100K', bg: '#E8F4F8', text: '#1976D2' },
    ];
    
    return ranges[employees.length % ranges.length];
  };

  const filteredCompanies = companies.filter((company) => {
    const matchesSearch = company.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         company.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         company.location?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const isAfrican = africanCountries.some(country => 
      company.location?.toLowerCase().includes(country.toLowerCase())
    );
    
    const matchesTab = activeTab === 'all' ? true :
                      activeTab === 'african' ? isAfrican :
                      !isAfrican;
    
    return matchesSearch && matchesTab;
  });

  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);

  const toggleCompanySelection = (id: number) => {
    setSelectedCompanies(prev =>
      prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCompanies.length === paginatedCompanies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(paginatedCompanies.map(c => c.id));
    }
  };

  if (isLoading) {
    return (
      <UserSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading companies...</p>
          </div>
        </div>
      </UserSidebarLayout>
    );
  }

  return (
    <UserSidebarLayout>
      <div className="min-h-screen bg-white">
        <div className="max-w-[1400px] mx-auto px-8 py-8">
      {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>
            Companies
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-8 mb-6 border-b" style={{ borderColor: '#E0E0E0' }}>
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-3 font-medium transition-all ${activeTab === 'all' ? 'border-b-2' : ''}`}
            style={{
              color: activeTab === 'all' ? COLORS.user.primary : '#666',
              borderColor: activeTab === 'all' ? COLORS.user.primary : 'transparent'
            }}
          >
            All Companies
          </button>
          <button
            onClick={() => setActiveTab('african')}
            className={`pb-3 font-medium transition-all ${activeTab === 'african' ? 'border-b-2' : ''}`}
            style={{
              color: activeTab === 'african' ? COLORS.user.primary : '#666',
              borderColor: activeTab === 'african' ? COLORS.user.primary : 'transparent'
            }}
          >
            African Companies
          </button>
        <button
            onClick={() => setActiveTab('non-african')}
            className={`pb-3 font-medium transition-all ${activeTab === 'non-african' ? 'border-b-2' : ''}`}
            style={{
              color: activeTab === 'non-african' ? COLORS.user.primary : '#666',
              borderColor: activeTab === 'non-african' ? COLORS.user.primary : 'transparent'
            }}
          >
            Non African Companies
        </button>
      </div>

        {/* View Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'table' ? 'shadow-sm' : ''}`}
              style={{ 
                color: viewMode === 'table' ? '#1A1A1A' : '#666',
                backgroundColor: viewMode === 'table' ? '#FAFAFA' : 'transparent'
              }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Table
            </button>
            <button
              onClick={() => setViewMode('board')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'board' ? 'shadow-sm' : ''}`}
              style={{ 
                color: viewMode === 'board' ? '#1A1A1A' : '#666',
                backgroundColor: viewMode === 'board' ? '#FAFAFA' : 'transparent'
              }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'list' ? 'shadow-sm' : ''}`}
              style={{ 
                color: viewMode === 'list' ? '#1A1A1A' : '#666',
                backgroundColor: viewMode === 'list' ? '#FAFAFA' : 'transparent'
              }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              List
            </button>
      </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-violet-500"
                style={{ borderColor: '#E0E0E0' }}
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50" style={{ borderColor: '#E0E0E0' }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter
            </button>
          </div>
        </div>

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="rounded-lg shadow-sm overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
            <table className="w-full">
              <thead style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E0E0E0' }}>
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCompanies.length === paginatedCompanies.length && paginatedCompanies.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Company
                    <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5-5 5 5H5z" />
                    </svg>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Industry
                    <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5-5 5 5H5z" />
                    </svg>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Location
                    <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5-5 5 5H5z" />
                    </svg>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Status
                    <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5-5 5 5H5z" />
                    </svg>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Contact person
                    <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5-5 5 5H5z" />
                    </svg>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Employee Range
                    <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 8l5-5 5 5H5z" />
                    </svg>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.map((company, index) => {
                  const statusBadge = getStatusBadge(company.is_active, index);
                  const employeeRange = getEmployeeRange(company.employees);
                  
                  return (
                    <tr
              key={company.id}
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      style={{ borderColor: '#F0F0F0' }}
              onClick={() => navigate(`/dashboard/companies/${company.id}`)}
            >
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(company.id)}
                          onChange={() => toggleCompanySelection(company.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                            style={{ backgroundColor: getInitialColor(company.name) }}
                          >
                            {getCompanyInitial(company.name)}
                          </div>
                          <span className="font-medium" style={{ color: '#1A1A1A' }}>
                            {company.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {company.industry || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            {company.location || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: statusBadge.bg, color: statusBadge.text }}
                        >
                          {statusBadge.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                            style={{ backgroundColor: COLORS.user.queryButton }}
                          >
                            {company.created_by?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <span className="text-sm text-gray-600">
                            {company.created_by?.split('-')[0] || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: employeeRange.bg, color: employeeRange.text }}
                        >
                          {employeeRange.range}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Board View */}
        {viewMode === 'board' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginatedCompanies.map((company, index) => {
              const statusBadge = getStatusBadge(company.is_active, index);
              const employeeRange = getEmployeeRange(company.employees);
              
              return (
                <div
                  key={company.id}
                  onClick={() => navigate(`/dashboard/companies/${company.id}`)}
                  className="rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer border"
                  style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold"
                      style={{ backgroundColor: getInitialColor(company.name) }}
                    >
                      {getCompanyInitial(company.name)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg" style={{ color: '#1A1A1A' }}>
                        {company.name}
                      </h3>
                      <p className="text-sm text-gray-500">{company.industry || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Location:</span>
                      <span className="font-medium" style={{ color: '#1A1A1A' }}>{company.location || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Status:</span>
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: statusBadge.bg, color: statusBadge.text }}
                      >
                        {statusBadge.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Employees:</span>
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: employeeRange.bg, color: employeeRange.text }}
                      >
                        {employeeRange.range}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="rounded-lg shadow-sm" style={{ backgroundColor: '#FAFAFA' }}>
            {paginatedCompanies.map((company, index) => {
              const statusBadge = getStatusBadge(company.is_active, index);
              const employeeRange = getEmployeeRange(company.employees);
              
              return (
                <div
                  key={company.id}
                  onClick={() => navigate(`/dashboard/companies/${company.id}`)}
                  className="flex items-center gap-6 p-6 border-b hover:bg-gray-50 cursor-pointer transition-colors"
                  style={{ borderColor: '#F0F0F0' }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold flex-shrink-0"
                    style={{ backgroundColor: getInitialColor(company.name) }}
                  >
                    {getCompanyInitial(company.name)}
              </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1" style={{ color: '#1A1A1A' }}>
                {company.name}
              </h3>
                    <p className="text-sm text-gray-500">{company.industry || 'N/A'} • {company.location || 'N/A'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: statusBadge.bg, color: statusBadge.text }}
                    >
                      {statusBadge.status}
                    </span>
                    <span
                      className="px-3 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: employeeRange.bg, color: employeeRange.text }}
                    >
                      {employeeRange.range}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#E0E0E0' }}
            >
              ← Previous
            </button>
            
            {[...Array(Math.min(10, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-lg font-medium ${currentPage === pageNum ? 'text-white' : 'hover:bg-gray-50'}`}
                    style={{
                      backgroundColor: currentPage === pageNum ? COLORS.user.primary : 'transparent',
                      color: currentPage === pageNum ? '#FFFFFF' : '#666'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                return <span key={pageNum} className="px-2">...</span>;
              }
              return null;
            })}

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#E0E0E0' }}
            >
              Next →
            </button>
          </div>
          )}
        </div>
      </div>
    </UserSidebarLayout>
  );
};
